<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'

type PolygonLayer = {
  rings: number[][][]
  color: string
}

const props = defineProps<{
  image: string
  polygons: Record<string, PolygonLayer> | null
}>()

const canvas = ref<HTMLCanvasElement | null>(null)

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function draw(): void {
  const ctx = canvas.value?.getContext('2d')
  if (!ctx || !canvas.value || !props.image) { return }

  const img = new Image()
  img.onload = () => {
    const el = canvas.value!
    el.width = img.width
    el.height = img.height

    ctx.drawImage(img, 0, 0)

    if (!props.polygons) { return }

    const drawOrder = ['parcel', 'lawn', 'house']

    for (const key of drawOrder) {
      const layer = props.polygons[key] as PolygonLayer | undefined
      if (!layer?.rings?.length) { continue }

      for (const ring of layer.rings) {
        if (ring.length < 3) { continue }

        ctx.beginPath()
        ctx.moveTo(ring[0][0], ring[0][1])

        for (let i = 1; i < ring.length; i++) {
          ctx.lineTo(ring[i][0], ring[i][1])
        }

        ctx.closePath()
        ctx.fillStyle = hexToRgba(layer.color, 0.2)
        ctx.fill()
        ctx.strokeStyle = layer.color
        ctx.lineWidth = 2
        ctx.stroke()
      }
    }
  }

  img.src = props.image.startsWith('data:')
    ? props.image
    : `data:image/jpeg;base64,${props.image}`
}

onMounted(draw)
watch(() => [props.image, props.polygons], draw, { deep: true })
</script>

<template>
  <canvas ref="canvas" class="size-full object-cover" />
</template>
